// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {createVoiceAudioContext} from '@app/features/voice/engine/VoiceSharedAudioContext';
import {
    detectWasmSimdSupport,
    resolveNoiseSuppressionContextSampleRate,
} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionBackends';
import {resolveNoiseGateTuning} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionGateTuning';
import type * as NoiseSuppressionWorkletAssets from '@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets';
import {
    NOISE_SUPPRESSION_WORKLET_PROCESSOR_NAMES,
    type NoiseSuppressionWorkletBackend,
} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletTypes';

const logger = new Logger('NoiseSuppressionChain');

/** Hard upper bound for building the complete chain. This is failure protection, not a warm-up delay. */
export const NOISE_SUPPRESSION_STARTUP_TIMEOUT_MS = 8_000;
/** A correctly initialized local worklet should acknowledge readiness quickly. */
const WORKLET_READY_TIMEOUT_MS = 4_000;
const PRELOAD_SAMPLE_RATE = 48_000;

export interface NoiseSuppressionWorkletChain {
    inputDestination: MediaStreamAudioDestinationNode;
    processedTrack: MediaStreamTrack;
    dispose: () => Promise<void>;
}

interface WorkletSignal {
    type: 'ready' | 'error';
    message?: string;
}

export interface NoiseSuppressionWorkletOptions {
    audioContext: AudioContext;
    backend: NoiseSuppressionWorkletBackend;
    suppressionStrength: number;
    signal?: AbortSignal;
    onRuntimeFailure?: (error: Error) => void;
}

interface WorkletResources {
    inputDestination: MediaStreamAudioDestinationNode;
    feedTrack: MediaStreamTrack;
    context: AudioContext;
    node?: AudioWorkletNode;
    source?: MediaStreamAudioSourceNode;
    output?: MediaStreamAudioDestinationNode;
    processedTrack?: MediaStreamTrack;
    disposal?: Promise<void>;
}

interface WarmContextSlot {
    backend: NoiseSuppressionWorkletBackend;
    sampleRate: number;
    context: AudioContext;
    moduleUrl: string;
}

/** Raw bytes are immutable in our cache. Always clone before transferring to a processor. */
const wasmBinaries = new Map<string, ArrayBuffer>();
/** Compilation promises deduplicate concurrent preload/build calls and prime the browser WASM compiler cache. */
const wasmCompilePromises = new Map<string, Promise<WebAssembly.Module>>();
let warmContextSlot: WarmContextSlot | null = null;

function isWorkletSignal(value: unknown): value is WorkletSignal {
    if (!value || typeof value !== 'object') return false;
    const record = value as Record<string, unknown>;
    if (record.message !== undefined && typeof record.message !== 'string') return false;
    return record.type === 'ready' || record.type === 'error';
}

function resolveWasmUrl(
    assets: typeof NoiseSuppressionWorkletAssets,
    backend: NoiseSuppressionWorkletBackend,
): string | null {
    switch (backend) {
        case 'gate':
            return null;
        case 'speex':
            return assets.NOISE_SUPPRESSION_WASM_URLS.speex;
        case 'gtcrn':
            return assets.NOISE_SUPPRESSION_WASM_URLS.gtcrn;
        case 'rnnoise':
            return detectWasmSimdSupport()
                ? assets.NOISE_SUPPRESSION_WASM_URLS.rnnoiseSimd
                : assets.NOISE_SUPPRESSION_WASM_URLS.rnnoise;
    }
}

function buildProcessorOptions(
    backend: NoiseSuppressionWorkletBackend,
    suppressionStrength: number,
    wasmBinary: ArrayBuffer | null,
): Record<string, unknown> {
    if (backend === 'gate') {
        const tuning = resolveNoiseGateTuning(suppressionStrength);
        return {
            openThreshold: tuning.openThreshold,
            closeThreshold: tuning.closeThreshold,
            holdMs: tuning.holdMs,
            maxChannels: 1,
        };
    }

    return {maxChannels: 1, wasmBinary};
}

function safeDisconnect(node: AudioNode | null | undefined): void {
    if (!node) return;
    try {
        node.disconnect();
    } catch {}
}

function safeStopTrack(track: MediaStreamTrack | null | undefined): void {
    if (!track) return;
    try {
        track.stop();
    } catch {}
}

async function closeContext(context: AudioContext | null | undefined): Promise<void> {
    if (!context || context.state === 'closed') return;
    try {
        await context.close();
    } catch {}
}

function abortError(signal: AbortSignal): unknown {
    return signal.reason ?? new DOMException('Operation aborted', 'AbortError');
}

function awaitStartupStep<T>(pending: Promise<T>, signal: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        if (signal.aborted) {
            reject(abortError(signal));
            return;
        }

        const onAbort = () => {
            cleanup();
            reject(abortError(signal));
        };
        const cleanup = () => signal.removeEventListener('abort', onAbort);
        signal.addEventListener('abort', onAbort, {once: true});

        pending.then(
            (value) => {
                cleanup();
                resolve(value);
            },
            (error: unknown) => {
                cleanup();
                reject(error);
            },
        );
    });
}

async function fetchWasmBinary(url: string, signal: AbortSignal): Promise<ArrayBuffer> {
    const cached = wasmBinaries.get(url);
    if (cached?.byteLength) return cached.slice(0);

    const response = await fetch(url, {credentials: 'same-origin', signal});
    if (!response.ok) {
        throw new Error(`Noise suppression asset request failed with status ${response.status}`);
    }

    const binary = await response.arrayBuffer();
    signal.throwIfAborted();
    if (binary.byteLength === 0) throw new Error('Noise suppression WASM asset is empty');

    wasmBinaries.set(url, binary.slice(0));
    return binary.slice(0);
}

async function primeWasmCompilation(url: string, binary: ArrayBuffer, signal: AbortSignal): Promise<void> {
    let pending = wasmCompilePromises.get(url);
    if (!pending) {
        pending = WebAssembly.compile(binary.slice(0));
        wasmCompilePromises.set(url, pending);
        pending.catch(() => wasmCompilePromises.delete(url));
    }
    await awaitStartupStep(pending, signal);
}

function takeWarmContext(backend: NoiseSuppressionWorkletBackend, sampleRate: number): AudioContext | null {
    const slot = warmContextSlot;
    if (!slot || slot.backend !== backend || slot.sampleRate !== sampleRate || slot.context.state === 'closed') {
        return null;
    }
    warmContextSlot = null;
    return slot.context;
}

async function retainWarmContext(slot: WarmContextSlot): Promise<void> {
    const previous = warmContextSlot;
    warmContextSlot = slot;
    if (previous && previous.context !== slot.context) await closeContext(previous.context);
}

export function isWarmNoiseSuppressionWorkletContextAlive(backend: NoiseSuppressionWorkletBackend): boolean {
    const slot = warmContextSlot;
    return slot != null && slot.backend === backend && slot.context.state !== 'closed';
}

function createContextForRate(sampleRate: number): AudioContext | null {
    const context = createVoiceAudioContext({latencyHint: 'interactive', sampleRate});
    if (!context) return null;
    if (context.sampleRate !== sampleRate) {
        void closeContext(context);
        return null;
    }
    return context;
}

function resolveNoiseSuppressionWorkletContext(
    backend: NoiseSuppressionWorkletBackend,
    captureContext: AudioContext,
    feedTrack: MediaStreamTrack,
): AudioContext | null {
    const targetSampleRate = resolveNoiseSuppressionContextSampleRate(backend, captureContext.sampleRate);
    const warmed = takeWarmContext(backend, targetSampleRate);
    if (warmed) return warmed;

    const context = createContextForRate(targetSampleRate);
    if (!context) return null;

    // Validate that this browser can bridge the MediaStream into the model-rate context.
    try {
        const probe = context.createMediaStreamSource(new MediaStream([feedTrack]));
        probe.disconnect();
        return context;
    } catch (error) {
        logger.info('Noise suppression cannot read the capture graph at the model rate', {
            backend,
            targetSampleRate,
            captureSampleRate: captureContext.sampleRate,
            error,
        });
        void closeContext(context);
        return null;
    }
}

function awaitWorkletReady(
	node: AudioWorkletNode,
	backend: NoiseSuppressionWorkletBackend,
	signal: AbortSignal,
): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		let settled = false;
		let timeoutId: number | undefined;

		const finish = (error?: unknown) => {
			if (settled) return;
			settled = true;
			if (timeoutId !== undefined) window.clearTimeout(timeoutId);
			node.port.removeEventListener('message', onMessage);
			node.removeEventListener('processorerror', onProcessorError);
			signal.removeEventListener('abort', onAbort);
			if (error !== undefined) reject(error);
			else resolve();
		};

		const onAbort = () => finish(abortError(signal));
		const onProcessorError = () =>
			finish(new Error(`Noise suppression worklet "${backend}" raised a processor error during startup`));
		const onMessage = (event: MessageEvent) => {
			const data = event.data;
			if (!isWorkletSignal(data)) return;
			if (data.type === 'ready') {
				finish();
				return;
			}
			finish(new Error(`Noise suppression worklet "${backend}" failed: ${data.message ?? 'unknown error'}`));
		};

		if (signal.aborted) {
			onAbort();
			return;
		}

		signal.addEventListener('abort', onAbort, {once: true});
		// Attach before yielding so a fast (preloaded) WASM ready cannot race past us.
		node.port.addEventListener('message', onMessage);
		node.addEventListener('processorerror', onProcessorError, {once: true});
		try {
			node.port.start();
		} catch {}

		timeoutId = window.setTimeout(() => {
			finish(
				new Error(
					`Noise suppression worklet "${backend}" did not emit a ready signal within ${WORKLET_READY_TIMEOUT_MS} ms`,
				),
			);
		}, WORKLET_READY_TIMEOUT_MS);
	});
}

function createWorkletResources(audioContext: AudioContext, backend: NoiseSuppressionWorkletBackend): WorkletResources {
    const inputDestination = audioContext.createMediaStreamDestination();
    inputDestination.channelCount = 1;
    inputDestination.channelCountMode = 'explicit';
    inputDestination.channelInterpretation = 'speakers';

    const feedTrack = inputDestination.stream.getAudioTracks()[0];
    if (!feedTrack) {
        safeDisconnect(inputDestination);
        throw new Error('buildNoiseSuppressionWorkletChain: missing capture feed track');
    }

    const context = resolveNoiseSuppressionWorkletContext(backend, audioContext, feedTrack);
    if (!context) {
        safeDisconnect(inputDestination);
        safeStopTrack(feedTrack);
        throw new Error(`buildNoiseSuppressionWorkletChain: no usable audio context for "${backend}"`);
    }

    return {inputDestination, feedTrack, context};
}

async function releaseWorkletResources(resources: WorkletResources): Promise<void> {
    const {node, source, output, inputDestination, feedTrack, processedTrack, context} = resources;

    if (node) {
        node.port.onmessage = null;
        node.onprocessorerror = null;
        try {
            node.port.postMessage('destroy');
            node.port.close();
        } catch {}
    }

    safeDisconnect(source);
    safeDisconnect(node);
    safeDisconnect(output);
    safeDisconnect(inputDestination);
    safeStopTrack(processedTrack);
    safeStopTrack(feedTrack);
    await closeContext(context);
}

function disposeWorkletResources(resources: WorkletResources): Promise<void> {
    resources.disposal ??= releaseWorkletResources(resources);
    return resources.disposal;
}

function wireFailureHandlers(
	resources: WorkletResources,
	backend: NoiseSuppressionWorkletBackend,
	onRuntimeFailure?: (error: Error) => void,
): void {
	const node = resources.node;
	if (!node) return;
	let failed = false;

	const reportFailure = (error: Error) => {
		if (failed || resources.disposal) return;
		failed = true;
		onRuntimeFailure?.(error);
	};

	node.port.addEventListener('message', (event: MessageEvent) => {
		const data = event.data;
		if (!isWorkletSignal(data) || data.type !== 'error') return;
		reportFailure(new Error(`Noise suppression worklet "${backend}" failed: ${data.message ?? 'unknown error'}`));
	});
	node.addEventListener('processorerror', () =>
		reportFailure(new Error(`Noise suppression worklet "${backend}" raised a processor error`)),
	);
}

async function installWorklet(
	resources: WorkletResources,
	options: NoiseSuppressionWorkletOptions,
	signal: AbortSignal,
): Promise<void> {
	const {backend, suppressionStrength} = options;

	const assets = await awaitStartupStep(
		import('@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets'),
		signal,
	);
	signal.throwIfAborted();

	const wasmUrl = resolveWasmUrl(assets, backend);
	const workletModuleUrl = assets.NOISE_SUPPRESSION_WORKLET_MODULE_URLS[backend];

	if (resources.context.state === 'suspended') {
		await awaitStartupStep(resources.context.resume(), signal);
	}

	const output = resources.context.createMediaStreamDestination();
	output.channelCount = 1;
	output.channelCountMode = 'explicit';
	output.channelInterpretation = 'speakers';
	resources.output = output;
	resources.processedTrack = output.stream.getAudioTracks()[0];
	if (!resources.processedTrack) {
		throw new Error('buildNoiseSuppressionWorkletChain: missing processed output track');
	}

	resources.source = resources.context.createMediaStreamSource(new MediaStream([resources.feedTrack]));

	const [wasmBinary] = await awaitStartupStep(
		Promise.all([
			wasmUrl === null ? Promise.resolve(null) : fetchWasmBinary(wasmUrl, signal),
			resources.context.audioWorklet.addModule(workletModuleUrl).then(() => undefined),
		]).then(([binary]) => [binary] as const),
		signal,
	);
	signal.throwIfAborted();

	if (wasmUrl && wasmBinary) await primeWasmCompilation(wasmUrl, wasmBinary, signal);

	const node = new AudioWorkletNode(resources.context, NOISE_SUPPRESSION_WORKLET_PROCESSOR_NAMES[backend], {
		numberOfInputs: 1,
		numberOfOutputs: 1,
		outputChannelCount: [1],
		channelCount: 1,
		channelCountMode: 'explicit',
		channelInterpretation: 'speakers',
		processorOptions: buildProcessorOptions(backend, suppressionStrength, wasmBinary),
	});
	resources.node = node;

	await awaitWorkletReady(node, backend, signal);
	signal.throwIfAborted();

	resources.source.connect(node);
	node.connect(output);
	wireFailureHandlers(resources, backend, options.onRuntimeFailure);

	logger.info('Noise suppression worklet ready', {
		backend,
		sampleRate: resources.context.sampleRate,
	});
}

/**
 * Preload immutable assets and a model-rate AudioContext/worklet module.
 * This deliberately does NOT run fake speech, retain recurrent model state,
 * or wait an arbitrary "adaptation" period.
 */
export async function preloadNoiseSuppressionWorkletAssets(
    backend: NoiseSuppressionWorkletBackend,
    signal?: AbortSignal,
): Promise<void> {
    const controller = new AbortController();
    const onAbort = () => controller.abort(signal?.reason);
    signal?.addEventListener('abort', onAbort, {once: true});
    if (signal?.aborted) controller.abort(signal.reason);

    try {
        if (isWarmNoiseSuppressionWorkletContextAlive(backend)) return;

        const assets = await awaitStartupStep(
            import('@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets'),
            controller.signal,
        );
        controller.signal.throwIfAborted();

        const wasmUrl = resolveWasmUrl(assets, backend);
        const workletModuleUrl = assets.NOISE_SUPPRESSION_WORKLET_MODULE_URLS[backend];
        const targetSampleRate = resolveNoiseSuppressionContextSampleRate(backend, PRELOAD_SAMPLE_RATE);

        const startedAt = performance.now();
        let wasmBinary: ArrayBuffer | null = null;
        if (wasmUrl) {
            wasmBinary = await fetchWasmBinary(wasmUrl, controller.signal);
            await primeWasmCompilation(wasmUrl, wasmBinary, controller.signal);
        }

        const context = createContextForRate(targetSampleRate);
        if (!context) return;

        try {
            if (context.state === 'suspended') await context.resume().catch(() => undefined);
            await awaitStartupStep(context.audioWorklet.addModule(workletModuleUrl), controller.signal);
            controller.signal.throwIfAborted();
            await retainWarmContext({backend, sampleRate: context.sampleRate, context, moduleUrl: workletModuleUrl});
        } catch (error) {
            await closeContext(context);
            throw error;
        }

        logger.info('Noise suppression assets preloaded', {
            backend,
            sampleRate: targetSampleRate,
            wasmBytes: wasmBinary?.byteLength ?? 0,
            elapsedMs: Math.round((performance.now() - startedAt) * 10) / 10,
        });
    } finally {
        signal?.removeEventListener('abort', onAbort);
    }
}

export async function buildNoiseSuppressionWorkletChain(
    options: NoiseSuppressionWorkletOptions,
): Promise<NoiseSuppressionWorkletChain> {
    options.signal?.throwIfAborted();

    const controller = new AbortController();
    const onAbort = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener('abort', onAbort, {once: true});
    const timeoutId = window.setTimeout(() => {
        controller.abort(new Error(`Noise suppression worklet "${options.backend}" startup timed out`));
    }, NOISE_SUPPRESSION_STARTUP_TIMEOUT_MS);

    let resources: WorkletResources | undefined;
    const startedAt = performance.now();

    try {
        resources = createWorkletResources(options.audioContext, options.backend);
        await installWorklet(resources, options, controller.signal);

        const {inputDestination, processedTrack} = resources;
        if (!processedTrack) {
            throw new Error('buildNoiseSuppressionWorkletChain: missing processed output track');
        }

        logger.info('Noise suppression chain built', {
            backend: options.backend,
            elapsedMs: Math.round((performance.now() - startedAt) * 10) / 10,
        });

        const readyResources = resources;
        return {
            inputDestination,
            processedTrack,
            dispose: () => disposeWorkletResources(readyResources),
        };
	} catch (error) {
		controller.abort(error);
		if (resources) await disposeWorkletResources(resources).catch(() => undefined);
		if (options.backend === 'gtcrn') {
			console.warn('[GTCRN] initialization failed', error);
			console.warn('[GTCRN] continuing without suppression');
		}
		throw error;
	} finally {
		window.clearTimeout(timeoutId);
		options.signal?.removeEventListener('abort', onAbort);
	}
}

/** A suppressor living inside its own model-rate AudioContext; callers wire their whole input graph into it. */
export interface NoiseSuppressionStage {
	context: AudioContext;
	input: AudioNode;
	output: AudioNode;
	dispose: () => Promise<void>;
}

/**
 * Builds the suppressor as a plain node in a dedicated context so the caller can run mic → gain → suppressor →
 * speech gate → output in ONE AudioContext. No MediaStream bridges between contexts: those have independent
 * clocks, drift and grow latency when one side stalls (minimized app), which caused dropouts and seconds of delay.
 * Throws when the raw capture track cannot be read at the model rate; callers then fall back to the legacy chain.
 */
export async function buildNoiseSuppressionWorkletStage(options: {
	backend: NoiseSuppressionWorkletBackend;
	suppressionStrength: number;
	captureTrack: MediaStreamTrack;
	captureSampleRate: number;
	signal?: AbortSignal;
	onRuntimeFailure?: (error: Error) => void;
}): Promise<NoiseSuppressionStage> {
	const {backend} = options;
	options.signal?.throwIfAborted();
	const controller = new AbortController();
	const onAbort = () => controller.abort(options.signal?.reason);
	options.signal?.addEventListener('abort', onAbort, {once: true});
	const timeoutId = window.setTimeout(() => {
		controller.abort(new Error(`Noise suppression worklet "${backend}" startup timed out`));
	}, NOISE_SUPPRESSION_STARTUP_TIMEOUT_MS);
	const signal = controller.signal;
	const targetSampleRate = resolveNoiseSuppressionContextSampleRate(backend, options.captureSampleRate);
	const context = takeWarmContext(backend, targetSampleRate) ?? createContextForRate(targetSampleRate);
	let node: AudioWorkletNode | null = null;
	let disposed = false;
	const dispose = async () => {
		if (disposed) return;
		disposed = true;
		if (node) {
			node.port.onmessage = null;
			try {
				node.port.postMessage('destroy');
				node.port.close();
			} catch {}
			safeDisconnect(node);
		}
		await closeContext(context);
	};
	try {
		if (!context) throw new Error(`No usable audio context for "${backend}"`);
		// Probe: the raw capture track must be readable at the model rate.
		context.createMediaStreamSource(new MediaStream([options.captureTrack])).disconnect();
		if (context.state === 'suspended') await awaitStartupStep(context.resume(), signal);
		const assets = await awaitStartupStep(
			import('@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets'),
			signal,
		);
		const wasmUrl = resolveWasmUrl(assets, backend);
		const [wasmBinary] = await awaitStartupStep(
			Promise.all([
				wasmUrl === null ? Promise.resolve(null) : fetchWasmBinary(wasmUrl, signal),
				context.audioWorklet.addModule(assets.NOISE_SUPPRESSION_WORKLET_MODULE_URLS[backend]).then(() => undefined),
			]).then(([binary]) => [binary] as const),
			signal,
		);
		if (wasmUrl && wasmBinary) await primeWasmCompilation(wasmUrl, wasmBinary, signal);
		node = new AudioWorkletNode(context, NOISE_SUPPRESSION_WORKLET_PROCESSOR_NAMES[backend], {
			numberOfInputs: 1,
			numberOfOutputs: 1,
			outputChannelCount: [1],
			channelCount: 1,
			channelCountMode: 'explicit',
			channelInterpretation: 'speakers',
			processorOptions: buildProcessorOptions(backend, options.suppressionStrength, wasmBinary),
		});
		await awaitWorkletReady(node, backend, signal);
		const readyNode = node;
		let failed = false;
		const reportFailure = (error: Error) => {
			if (failed || disposed) return;
			failed = true;
			options.onRuntimeFailure?.(error);
		};
		readyNode.port.addEventListener('message', (event: MessageEvent) => {
			const data = event.data;
			if (!isWorkletSignal(data) || data.type !== 'error') return;
			reportFailure(new Error(`Noise suppression worklet "${backend}" failed: ${data.message ?? 'unknown error'}`));
		});
		readyNode.addEventListener('processorerror', () =>
			reportFailure(new Error(`Noise suppression worklet "${backend}" raised a processor error`)),
		);
		logger.info('Noise suppression stage ready', {backend, sampleRate: context.sampleRate});
		return {context, input: readyNode, output: readyNode, dispose};
	} catch (error) {
		await dispose();
		throw error;
	} finally {
		window.clearTimeout(timeoutId);
		options.signal?.removeEventListener('abort', onAbort);
	}
}
